import { Constants } from "@/api/constants";
import { getQuizAnswers, getSSLocalStorage } from "@/utils/localStorage";
import { getQuizDataFromLS } from "@/utils/quiz";

export const businessIDs = {
  consumerAffairs: "consumeraffairs",
  bold: "bold",
  mhs: "mhs",
  madison: "madison-medical-affiliates",
  ripple: "ripple-pt",
  titleBoxing: "title-boxing-club",
  liveItUp: "live-it-up",
  jobTest: "jobtest",
  lookMed: "lookmed",
  apartmentList: "apartmentlist",
  bma: "bma", // Bergen Medical Associates
  sworn: "sworn",
  cycling: "cycling-club",
  tripleBypass: "triple-bypass",
} as const;

export type BusinessKeys = keyof typeof businessIDs;
export type BusinessValues = (typeof businessIDs)[BusinessKeys];

// Flag to track if debug has been logged
let hasLoggedBusinessNameDebug = false;

const extractBusinessFromPath = (path: string): BusinessValues | undefined => {
  if (!path) return undefined;

  if (path.includes("cycling")) return businessIDs.cycling;
  if (path.includes("triplebypass")) return businessIDs.tripleBypass;

  if (path?.includes("/business/")) {
    const segments = path.split("/");
    return segments[segments.length - 1] as BusinessValues;
  }

  if(path?.includes("/business-live-it-up")) {
    return businessIDs.liveItUp;
  }
  
  return undefined;
};

const getBusinessNameFromLP = (): BusinessValues | undefined => {
  try {
    const constants = new Constants();
    const lsKey = constants.getConst("localStorageBaseKey");
    const ls = localStorage.getItem(lsKey);

    if (!ls) return undefined;

    const lsObj = JSON.parse(ls);

    // Try landingPage first
    const currentLP = lsObj?.land?.landingPage as string;
    let businessName = extractBusinessFromPath(currentLP);
    if (businessName) {
      return businessName;
    }

    // If no business name found, try landingPageInitial
    const initialLP = lsObj?.land?.landingPageInitial as string;
    businessName = extractBusinessFromPath(initialLP);
    if (businessName) {
      return businessName;
    }

    return undefined;
  } catch (e) {
    console.error("Error getting business name from landing page:", e);
    return undefined;
  }
};

const getBusinessNameFromQuiz = (): BusinessValues | undefined => {
  try {
    const quizData = getQuizDataFromLS();
    if (!quizData?.title) return undefined;

    const match = quizData.title.match(/\((.*?)\)/);
    if (!match || !match[1]) {
      return undefined;
    }

    const businessName = match[1].toLowerCase().split(/\s+/).join("-") as BusinessValues;
    return businessName;
  } catch (e) {
    console.error("Error getting business name from quiz:", e);
    return undefined;
  }
};

const getBusinessNameFromCurrentPath = (): BusinessValues | undefined => {
  try {
    const path = window.location.pathname;
    const match = path.match(/\/quiz-business-(.+)$/);
    if (!match || !match[1]) {
      return undefined;
    }
    return match[1] as BusinessValues;
  } catch (e) {
    console.error("Error getting business name from path:", e);
    return undefined;
  }
};

const getBusinessNameFromPathAnywhere = (): BusinessValues | undefined => {
  try {
    const path = window.location.pathname;
    const businessValues = Object.values(businessIDs);
    for (const businessId of businessValues) {
      if (path.includes(businessId)) {
        return businessId;
      }
    }
    return undefined;
  } catch (e) {
    console.error("Error getting business name from path anywhere:", e);
    return undefined;
  }
};

const getBusinessNameFromSSLocalStorage = (): BusinessValues | undefined => {
  try {
    const ssData = getSSLocalStorage();
    const businessValues = Object.values(businessIDs);
    const businessName = ssData?.appointmentData.business_name;
    if (businessName) {
      if (businessValues.includes(businessName)) {
        return businessName;
      }
    }
    return undefined;
  } catch (e) {
    console.error("Error getting business name from SS local storage", e);
    return undefined;
  }
};

type BusinessNameResolver = () => BusinessValues | undefined;

const tryGetBusinessName = (() => {
  const logResolution = (attempts: [string, string | undefined][], finalBusinessName: BusinessValues | undefined) => {
    if (!hasLoggedBusinessNameDebug) {
      console.debug("Business name resolution:", { attempts, final: finalBusinessName });
      hasLoggedBusinessNameDebug = true;
    }
    if (finalBusinessName && !Object.values(businessIDs).includes(finalBusinessName)) {
      console.warn(`Unknown business ID: ${finalBusinessName}`);
    }
  };

  return (
    method: string,
    resolver: BusinessNameResolver,
    attempts: [string, string | undefined][],
  ): BusinessValues | undefined => {
    const businessName = resolver();
    attempts.push([method, businessName]);

    if (businessName && Object.values(businessIDs).includes(businessName)) {
      logResolution(attempts, businessName);
      return businessName;
    }

    return undefined;
  };
})();

export const getBusinessName = (): BusinessValues | undefined => {
  try {
    let businessName: BusinessValues | undefined;
    const attempts: [string, string | undefined][] = [];

    // 1, Try ss-local-storage
    businessName = tryGetBusinessName("ss-local-storage", getBusinessNameFromSSLocalStorage, attempts);
    if (businessName) return businessName;

    // 2. Try current path with quiz-business pattern
    businessName = tryGetBusinessName("path-quiz-business", getBusinessNameFromCurrentPath, attempts);
    if (businessName) return businessName;

    // 3. Try landing page
    businessName = tryGetBusinessName("landing page", getBusinessNameFromLP, attempts);
    if (businessName) return businessName;

    // 4. Try quiz data
    businessName = tryGetBusinessName("quiz", getBusinessNameFromQuiz, attempts);
    if (businessName) return businessName;

    // 5. Try current path anywhere pattern (most permissive check, so we do it last)
    businessName = tryGetBusinessName("path-anywhere", getBusinessNameFromPathAnywhere, attempts);
    if (businessName) return businessName;

    // Log final resolution if no business name was found
    if (!hasLoggedBusinessNameDebug) {
      console.debug("Business name resolution:", { attempts, final: undefined });
      hasLoggedBusinessNameDebug = true;
    }

    return undefined;
  } catch (e) {
    console.error("Error in getBusinessName:", e);
    return undefined;
  }
};

export const isBusiness = (businessId?: string): boolean => {
  const businessName = getBusinessName();
  // If no businessId is provided, check if any valid business ID is present
  if (businessId === undefined) {
    return businessName !== undefined;
  }
  // Otherwise, check for specific business match
  return businessName === businessId;
};

export const isCreditCardHidden = () => {
  const quizAnswers = getQuizAnswers();
  const apptData = getSSLocalStorage()?.appointmentData;
  const paymentMethod = quizAnswers?.payment_method || apptData?.payment_method || null;
  if (paymentMethod && paymentMethod === "insurance") {
    return (
      isBusiness(businessIDs.consumerAffairs) ||
      isBusiness(businessIDs.apartmentList) ||
      isBusiness(businessIDs.bma) ||
      isBusiness(businessIDs.bold) ||
      isBusiness(businessIDs.sworn) ||
      isBusiness(businessIDs.liveItUp)
    );
  } else {
    return false;
  }
};

export const isValidBusiness = isBusiness(businessIDs.bold) || isBusiness(businessIDs.consumerAffairs);
